import EntryMileageIcon from '@kijiji/icons/src/icons/EntryMileage'
import FuelIcon from '@kijiji/icons/src/icons/Fuel'
import LocationSolidIcon from '@kijiji/icons/src/icons/LocationSolid'
import TransmissionIcon from '@kijiji/icons/src/icons/Transmission'
import { formatWholeNumber } from '@kijiji/number/formatWholeNumber'
import { useTranslation } from 'next-i18next'
import { type Key, type ReactElement } from 'react'
import { useTheme } from 'styled-components'

import { ShowAtOrLarger, ShowWhenSmaller } from '@/components/shared/breakpoint'
import { AutosAttributeListItem } from '@/components/srp/listing-card/autos-attribute-list/AutosAttributeListItem'
import { AutosCrossPromoListingAttributesList } from '@/components/srp/listing-card/autos-x-promo-listing-card/styled'
import { TRANSLATION_KEYS } from '@/constants/localization'
import { ATTRIBUTES, CAR_TRANSMISSION_VALUES } from '@/features/attributes/constants/attributes'
import { ROUTE_LOCALE, useLocale } from '@/hooks/useLocale'
import { Flex } from '@/ui/atoms/flex'
import { capitalizeString } from '@/utils/string'

type AutosCrossPromoListingAttributesProps = {
  /**
   * Listing attributes
   */
  attributes: Record<string, string[]>
  /**
   * Listing location
   */
  locationName?: string | null
}

type CarAttribute = {
  label: string
  value: string
  icon: ReactElement
  hiddenOnSmallerThanMediumBreakpoint: boolean
}

export const AutosCrossPromoListingAttributes = ({
  attributes,
  locationName,
}: AutosCrossPromoListingAttributesProps) => {
  const { t } = useTranslation([TRANSLATION_KEYS.SRP, TRANSLATION_KEYS.LISTING])
  const theme = useTheme()
  const { routeLocale } = useLocale()

  const kilometers = attributes[ATTRIBUTES.CAR_MILEAGE]?.[0]
  const transmission = attributes[ATTRIBUTES.CAR_TRANSMISSION]?.[0]

  const transmissionLocaleKeyMap: Record<string, string> = {
    [CAR_TRANSMISSION_VALUES.MANUAL]: `${TRANSLATION_KEYS.LISTING}:attributes.car_transmission.manual`,
    [CAR_TRANSMISSION_VALUES.AUTOMATIC]: `${TRANSLATION_KEYS.LISTING}:attributes.car_transmission.automatic`,
  }

  const formattedKilometers = kilometers
    ? formatWholeNumber(Number(kilometers), routeLocale === ROUTE_LOCALE.en)
    : undefined
  const transmissionLocaleKey = transmission ? transmissionLocaleKeyMap[transmission] : undefined
  const fuelType = attributes[ATTRIBUTES.CAR_FUEL_TYPE]?.[0]

  const carAttributes: CarAttribute[] = [
    ...(formattedKilometers
      ? [
          {
            label: t(`${TRANSLATION_KEYS.SRP}:listing.attributes.kilometers.label`),
            value: t(`${TRANSLATION_KEYS.SRP}:listing.attributes.kilometers.value`, {
              kms: formattedKilometers,
            }),
            icon: <EntryMileageIcon size="default" color={theme.colors.grey.primary} />,
            hiddenOnSmallerThanMediumBreakpoint: false,
          },
        ]
      : []),
    ...(transmissionLocaleKey
      ? [
          {
            label: t(`${TRANSLATION_KEYS.LISTING}:attributes.car_transmission.label`),
            value: t(transmissionLocaleKey),
            hiddenOnSmallerThanMediumBreakpoint: true,
            icon: <TransmissionIcon size="default" color={theme.colors.grey.primary} />,
          },
        ]
      : []),
    ...(fuelType
      ? [
          {
            label: t(`${TRANSLATION_KEYS.SRP}:listing.attributes.fuel_type.label`),
            value: capitalizeString(fuelType),
            hiddenOnSmallerThanMediumBreakpoint: true,
            icon: <FuelIcon size="default" color={theme.colors.grey.primary} />,
          },
        ]
      : []),
    ...(locationName
      ? [
          {
            label: t(`${TRANSLATION_KEYS.SRP}:listing.attributes.location`),
            value: locationName,
            icon: <LocationSolidIcon size="default" color={theme.colors.grey.primary} />,
            hiddenOnSmallerThanMediumBreakpoint: false,
          },
        ]
      : []),
  ]

  const renderAttribute = (attribute: CarAttribute, key: Key) => {
    if (!attribute) return null
    return <AutosAttributeListItem key={key} icon={attribute.icon} text={attribute.value} />
  }

  return (
    <Flex flexGrow={1}>
      <ShowAtOrLarger breakpoint="medium">
        <AutosCrossPromoListingAttributesList>
          {carAttributes.map((attribute) => {
            const key = `${attribute.label}-large`
            return renderAttribute(attribute, key)
          })}
        </AutosCrossPromoListingAttributesList>
      </ShowAtOrLarger>
      <ShowWhenSmaller breakpoint="medium">
        <AutosCrossPromoListingAttributesList>
          {carAttributes
            .filter((attribute) => attribute && !attribute.hiddenOnSmallerThanMediumBreakpoint)
            .map((attribute) => {
              const key = `${attribute.label}-belowMedium`
              return renderAttribute(attribute, key)
            })}
        </AutosCrossPromoListingAttributesList>
      </ShowWhenSmaller>
    </Flex>
  )
}
